import { useState } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { Home, ClipboardList, History, User, WifiOff, Wifi, RefreshCw, Cloud, CloudOff } from 'lucide-react';
import { useAuth } from '@features/auth/hooks/useAuth';
import { useSync } from '@core/hooks/useSync';
import { NotificationBell } from '@components/common/NotificationBell';
import { cn } from '@lib/utils';

export default function TechnicianLayout() {
  const { user } = useAuth();
  const { isOnline, pendingCount, isSyncing, sync, hasPending } = useSync();
  const [showSyncDetails, setShowSyncDetails] = useState(false);

  const handleSyncClick = async () => {
    if (isSyncing || !isOnline) return;
    try {
      await sync();
    } catch (error) {
      console.error('Error sincronizando:', error);
    }
  };

  return (
    <div className="flex h-screen flex-col bg-gray-50">
      {/* Header con indicador de conexión */}
      <header className="bg-gradient-merino text-white shadow-lg">
        <div className="mx-auto flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-white px-2 py-1">
              <img
                src="/logo-merino.png"
                alt="Merino Control de Plagas"
                className="h-7 w-auto object-contain"
              />
            </div>
            <p className="text-xs text-white/80">
              {user?.nombre || 'Técnico'}
            </p>
          </div>
          
          {/* Indicadores de Sincronización */}
          <div className="flex items-center gap-2">
            <NotificationBell className="[&>button]:text-white [&>button]:hover:bg-white/20" />
            {/* Badge de sincronización con contador */}
            <button
              onClick={handleSyncClick}
              disabled={isSyncing || !isOnline}
              className={cn(
                'flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-all',
                isSyncing && 'bg-blue-500',
                !isSyncing && hasPending && isOnline && 'bg-blue-500 hover:bg-blue-400',
                !isSyncing && hasPending && !isOnline && 'bg-warning-500 animate-pulse',
                !hasPending && isOnline && 'bg-success-500',
                !hasPending && !isOnline && 'bg-warning-500'
              )}
            >
              {isSyncing ? (
                <>
                  <RefreshCw className="h-3 w-3 animate-spin" />
                  <span>Sincronizando</span>
                </>
              ) : hasPending ? (
                <>
                  {isOnline ? <Cloud className="h-3 w-3" /> : <CloudOff className="h-3 w-3" />}
                  <span>{pendingCount}</span>
                </>
              ) : (
                <>
                  {isOnline ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
                  <span>{isOnline ? 'Sync OK' : 'Offline'}</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Alerta persistente de offline o pendientes */}
        {(!isOnline || (hasPending && isOnline)) && (
          <div className={cn(
            'px-4 py-2 text-center text-xs',
            !isOnline ? 'bg-warning-600' : 'bg-blue-600'
          )}>
            {!isOnline ? (
              <>
                📴 Sin conexión
                {hasPending && ` - ${pendingCount} elemento${pendingCount > 1 ? 's' : ''} guardado${pendingCount > 1 ? 's' : ''} localmente`}
              </>
            ) : (
              <button onClick={handleSyncClick} disabled={isSyncing} className="hover:underline">
                {isSyncing 
                  ? 'Sincronizando...' 
                  : `${pendingCount} elemento${pendingCount > 1 ? 's' : ''} listo${pendingCount > 1 ? 's' : ''} para sincronizar - Toca para sincronizar`
                }
              </button>
            )}
          </div>
        )}
      </header>

      {/* Main Content - Scrollable */}
      <main className="flex-1 overflow-y-auto pb-20">
        <div className="mx-auto max-w-4xl">
          <Outlet />
        </div>
      </main>

      {/* Bottom Navigation - Mobile First */}
      <nav className="fixed bottom-0 left-0 right-0 border-t border-gray-200 bg-white shadow-top">
        <div className="mx-auto flex max-w-4xl items-center justify-around">
          <NavLink
            to="/technician"
            end
            className={({ isActive }) =>
              cn(
                'flex flex-1 flex-col items-center gap-1 py-3 text-xs transition-colors',
                isActive
                  ? 'text-merino-600'
                  : 'text-gray-500 hover:text-merino-600'
              )
            }
          >
            {({ isActive }) => (
              <>
                <Home className={cn('h-6 w-6', isActive && 'fill-current')} />
                <span className="font-medium">Inicio</span>
              </>
            )}
          </NavLink>

          <NavLink
            to="/technician/citas"
            className={({ isActive }) =>
              cn(
                'flex flex-1 flex-col items-center gap-1 py-3 text-xs transition-colors',
                isActive
                  ? 'text-merino-600'
                  : 'text-gray-500 hover:text-merino-600'
              )
            }
          >
            {({ isActive }) => (
              <>
                <ClipboardList className={cn('h-6 w-6', isActive && 'fill-current')} />
                <span className="font-medium">Citas</span>
              </>
            )}
          </NavLink>

          <NavLink
            to="/technician/historial"
            className={({ isActive }) =>
              cn(
                'flex flex-1 flex-col items-center gap-1 py-3 text-xs transition-colors',
                isActive
                  ? 'text-merino-600'
                  : 'text-gray-500 hover:text-merino-600'
              )
            }
          >
            {({ isActive }) => (
              <>
                <History className={cn('h-6 w-6', isActive && 'fill-current')} />
                <span className="font-medium">Historial</span>
              </>
            )}
          </NavLink>

          <NavLink
            to="/technician/perfil"
            className={({ isActive }) =>
              cn(
                'flex flex-1 flex-col items-center gap-1 py-3 text-xs transition-colors',
                isActive
                  ? 'text-merino-600'
                  : 'text-gray-500 hover:text-merino-600'
              )
            }
          >
            {({ isActive }) => (
              <>
                <User className={cn('h-6 w-6', isActive && 'fill-current')} />
                <span className="font-medium">Perfil</span>
              </>
            )}
          </NavLink>
        </div>
      </nav>
    </div>
  );
}
